import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import axios from 'axios'

import environment from '@/environments/environment.dev'

import { AuthenticationService } from './authentication'

/**
 * Options for HTTP requests
 */
export interface RequestOptions {
  /** Custom headers to include */
  headers?: Record<string, string>
  /** URL query parameters */
  params?: Record<string, unknown>
  /** Function to serialize query parameters */
  paramsSerializer?: AxiosRequestConfig['paramsSerializer']
  /** Request body */
  body?: unknown
  /** AbortSignal for cancellation */
  signal?: AbortSignal
  /** Custom transform function for request data */
  transformRequest?: (data: unknown) => unknown
  /** Whether to include credentials */
  withCredentials?: boolean
}

class HttpClient {
  private readonly client: AxiosInstance

  constructor(
    baseURL: string = environment.baseUrl,
    timeout = 1_800_000, // 30 minutes
  ) {
    this.client = axios.create({
      baseURL,
      timeout,
    })

    // Add authorization token to requests
    this.client.interceptors.request.use(async (config) => {
      if (!config.url) {
        throw new Error('URL must not be blank')
      }

      if (!config.headers.Authorization) {
        const tokens = await AuthenticationService.getTokens()
        const accessToken = tokens?.jwt.accessToken

        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`
        }
      }

      return config
    })
  }

  /**
   * Make an HTTP request
   * @param method - HTTP method
   * @param url - Request URL (appended to base URL)
   * @param options - Request options
   */
  async request<T = unknown>(
    method: string,
    url: string,
    options: RequestOptions = {},
  ): Promise<AxiosResponse<T>> {
    const { headers, params, paramsSerializer, body, signal, transformRequest, withCredentials } =
      options

    const config: AxiosRequestConfig = {
      method,
      url,
      headers,
      params,
      paramsSerializer,
      data: body,
      signal,
      transformRequest,
      withCredentials,
    }

    return this.client.request<T>(config)
  }

  async get<T = unknown>(url: string, options: RequestOptions = {}): Promise<AxiosResponse<T>> {
    return this.request<T>('GET', url, options)
  }

  /**
   * Make a POST request
   */
  async post<T = unknown>(url: string, options: RequestOptions = {}): Promise<AxiosResponse<T>> {
    return this.request<T>('POST', url, options)
  }

  /**
   * Make a PUT request
   */
  async put<T = unknown>(url: string, options: RequestOptions = {}): Promise<AxiosResponse<T>> {
    return this.request<T>('PUT', url, options)
  }

  /**
   * Make a PATCH request
   */
  async patch<T = unknown>(url: string, options: RequestOptions = {}): Promise<AxiosResponse<T>> {
    return this.request<T>('PATCH', url, options)
  }

  /**
   * Make a DELETE request
   */
  async delete<T = unknown>(url: string, options: RequestOptions = {}): Promise<AxiosResponse<T>> {
    return this.request<T>('DELETE', url, options)
  }

  /**
   * Check if a request was cancelled
   */
  public isCancel(error: unknown): boolean {
    return axios.isCancel(error)
  }

  /**
   * Check if an error was caused by request abortion
   */
  public isAborted(error: unknown): boolean {
    return axios.isAxiosError(error) && error.code === 'ERR_CANCELED'
  }
}

export const httpClient = Object.freeze(new HttpClient())

export default HttpClient
